import { Placeholder, Text } from '@sitecore-jss/sitecore-jss-react';
import { useEffect, useRef, useState } from 'react';
import LazyImage from 'src/components/Common/LazyImage';
import SvgLoader from 'src/components/Common/SvgLoader';
import track from 'src/lib/Analytics';
import { useExperienceEditor } from 'src/lib/Hooks/useExperienceEditor';
import { PanelItemType } from './types';

// jss-public-pdp-item-content can contain ANY component
const PushDownPanelItem = ({
  analytics,
  classes,
  id,
  image,
  props,
  rendering,
  title,
}: PanelItemType) => {
  const [maxHeight, setMaxHeight] = useState(0);
  // The main h3 tag
  const containerRef = useRef<HTMLHeadingElement>(null);
  // Holds the content placeholder component
  const contentRef = useRef<HTMLDivElement>(null);
  const { isEEActive } = useExperienceEditor();
  const { activePanel, togglePanel } = props;
  const isActive = isEEActive || id === activePanel;
  const { buttonClass, containerClass, contentClass } = classes;

  const handleClick = () => {
    togglePanel(id);

    if (!isActive) {
      setTimeout(
        () =>
          containerRef.current &&
          // scroll up the window by the height of the pdp item
          window.scrollBy({
            top: containerRef.current.getBoundingClientRect().top - 0,
            behavior: 'smooth',
          }),
        300
      );

      track.component(analytics);
    }
  };

  useEffect(() => {
    // get the height of the content inside the content placeholder div
    if (contentRef?.current) {
      setMaxHeight(contentRef.current.scrollHeight);
    }
  }, [isActive]);

  return isEEActive ? (
    <div className="border-2 bg-teal-lighter">
      <span>Push Down Panel Item Content</span>
      <LazyImage {...image} className="js-image mb-8 icon-80 md:icon-128" />
      <Text
        className="text-teal-darker text-lg md:text-xl mb-16 group-hover:underline"
        field={title}
        tag="h3"
      />
      <Placeholder name="jss-public-pdp-item-content" {...{ rendering }} />
    </div>
  ) : (
    <>
      <h3
        className={`relative block py-16 md:py-24 w-1/2 sm:w-1/3 text-xl text-teal-darker ${containerClass}`}
        data-testid="panel-container"
        ref={containerRef}
      >
        <button
          aria-controls={`pushdown-panel-${id}`}
          aria-expanded={isActive}
          className={`relative flex flex-col items-center w-full h-full px-12 md:px-16 no-underline hover:underline ${buttonClass}`}
          data-testid="panel-button"
          id={`pushdown-trigger-${id}`}
          onClick={handleClick}
          type="button"
        >
          <LazyImage {...image} aria-hidden={true} className="js-image icon-80 md:icon-128" />
          <Text className="block flex-grow mt-8 lg:mt-12 line-clamp-4" field={title} tag="span" />
          <SvgLoader
            className={`mt-8 lg:mt-12 w-16 lg:w-20 transform duration-300 ${
              isActive ? 'scale-y-flip' : ''
            }`}
            color="text-blue"
            name="ArrowDown"
            sizeConstraints={false}
          />
        </button>
        <span
          className={`absolute right-0 bottom-0 left-0 -mb-16 transition-all ease-in-out duration-300 ${
            isActive ? 'opacity-1' : 'opacity-0'
          }`}
        >
          <SvgLoader
            className="mx-auto w-24"
            color="text-gray-lighter"
            name="Caret"
            sizeConstraints={false}
          />
        </span>
      </h3>
      <div
        aria-hidden={!isActive}
        aria-labelledby={`pushdown-trigger-${id}`}
        className={`-mx-16 md:-mx-24 xl:mx-0 w-screen xl:w-full overflow-hidden transition-all ease-in-out duration-300 bg-gray-lighter ${contentClass} ${
          isActive ? 'mt-16 visible' : 'invisible max-h-0'
        }`}
        data-testid="panel-content"
        id={`pushdown-panel-${id}`}
        ref={contentRef}
        role="region"
        style={isActive ? { maxHeight: `${maxHeight}px` } : {}}
      >
        <Placeholder name="jss-public-pdp-item-content" {...{ rendering }} />
        <div className="px-16 md:px-24 pb-24 md:pb-32 text-center">
          <button
            className="underline hover:no-underline text-teal-darker"
            onClick={handleClick}
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default PushDownPanelItem;
